import { action } from 'mobx'
import type { ISchemaBoundValidationContext, IValidationSchema } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { newCid } from '@/features/legends/editor/collections'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import { SymbolConnectorListViewModel } from '@/features/legends/symbols/SymbolConnectorListViewModel'
import type { SymbolConnectorViewModel } from '@/features/legends/symbols/SymbolConnectorViewModel'
import type { EffectEditorViewModel } from './EffectEditorViewModel'
import { validateObject } from '@/utils/validx-util'
import type { KnownMutator } from './effects'

export interface MutatorEditorViewModelOptions {
  effect: EffectEditorViewModel
  dependencies: LegendPartDependencies
}

/**
 * Base for mutators: changes to variables or service items, made by an
 * effect. Each type validates itself with its own schema.
 */
export abstract class MutatorEditorViewModelBase<
  TMutator extends KnownMutator = KnownMutator,
> extends LegendPartViewModelBase {
  protected readonly effect: EffectEditorViewModel

  /**
   * Identifies the mutator on the client, for keys.
   */
  readonly cid = newCid('mutator')

  /**
   * The mutator type, as stored in the legend.
   */
  abstract readonly type: TMutator['type']

  /**
   * The mutator's validation, bound to its type's schema.
   */
  abstract readonly validation: ISchemaBoundValidationContext<MutatorEditorViewModelBase>

  /**
   * What the mutator changes.
   */
  readonly targets: SymbolConnectorListViewModel

  /**
   * Validates `targets`. Each type's schema includes it.
   */
  protected readonly targetsSchema: IValidationSchema<MutatorEditorViewModelBase>

  /**
   * @param options
   * @param targetsMessage Shown when no target is picked.
   */
  constructor(
    { dependencies, effect }: MutatorEditorViewModelOptions,
    targetsMessage: string,
  ) {
    super(dependencies)
    this.effect = effect
    this.targetsSchema = { targets: [validateObject(targetsMessage)] }
    this.targets = new SymbolConnectorListViewModel({
      dependencies: this.dependencies,
      context: this.effect.context,
      required: true,
      predicates: [
        (descriptor, connector) =>
          this._isCompatibleTarget(
            descriptor,
            descriptor.entity.valueType ?? null,
            connector,
          ),
      ],
    })
    this.lifecycle.manage(this.targets)
    this.lifecycle.manage(
      this.targets.bubbleErrorsTo(this, this.targetsSchema, 'targets'),
    )
  }

  /**
   * Focuses the mutator's first input.
   */
  focus() {
    this.targets.focus()
  }

  /**
   * Sets the mutator from the legend document.
   */
  @action
  hydrate(mutator: TMutator) {
    this.targets.hydrate(mutator.targets)
  }

  /**
   * The mutator as the legend document stores it.
   */
  abstract toJSON(): TMutator

  /**
   * Shows the analyzer's errors for the mutator.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The effect's symbols, plus `Value`: the target's current value.
   */
  protected _symbolTableWithValue(valueType: RynoType) {
    const table = this.effect.symbolTable.scope('mutator')
    table.setIdentifierSymbolsFromObject({
      Value: [valueType, { constant: true }],
    })
    return table
  }

  /**
   * Whether a target's value is of the first target's type, which every
   * target must share once there is more than one.
   */
  protected _matchesFirstTarget(
    valueType: RynoType | null,
    connector: SymbolConnectorViewModel,
  ) {
    const [first] = this.targets.connectors
    if (this.targets.count <= 1 || first === connector) {
      return true
    }
    return Types.equalsType(first.valueType, valueType)
  }

  /**
   * Whether the entity can be a target. Variables by default.
   */
  protected _isCompatibleTarget(
    descriptor: EntityDescriptor,
    _valueType: RynoType | null,
    _connector: SymbolConnectorViewModel,
  ): boolean {
    return descriptor.type === 'Variable'
  }
}
